import React, { useEffect, useState, useRef } from 'react';
import { FileSpreadsheet, Download, Search, Loader2, Users, X } from 'lucide-react';
import { useApi } from './DashboardLayout';
import { API } from '../App';

function ProviderFilter({ providers, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selected = providers.find(p => p.id === value);
  const normalize = (s) => (s || '').toLowerCase().replace(/[-./,()]/g, ' ').replace(/\s+/g, ' ').trim();
  const filtered = query.length > 0
    ? (() => {
        const words = normalize(query).split(' ').filter(Boolean);
        return providers.filter(p => {
          const haystack = [p.verein, p.ik_number, p.club_id].map(normalize).join(' ');
          return words.every(w => haystack.includes(w));
        });
      })()
    : providers;

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      {selected ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1, padding: '7px 12px', fontSize: 13, fontWeight: 500, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--page-bg)', display: 'flex', alignItems: 'center', gap: 8 }} data-testid="export-provider-selected">
            <Users size={14} style={{ color: 'var(--primary)', flexShrink: 0 }} />
            {selected.verein}
            <span style={{ fontFamily: 'monospace', color: 'var(--text-muted)', fontSize: 11 }}>IK {selected.ik_number}</span>
          </div>
          <button type="button" onClick={() => onChange(null)} data-testid="export-provider-clear"
            style={{ padding: '6px 12px', fontSize: 11, fontWeight: 600, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--card-bg)', color: 'var(--text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
            <X size={12} /> Alle anzeigen
          </button>
        </div>
      ) : (
        <>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              style={{ width: '100%', padding: '7px 10px 7px 32px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--card-bg, #fff)', color: 'var(--text-primary)', boxSizing: 'border-box' }}
              value={query}
              onChange={e => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              placeholder="Anbieter suchen (Name, IK-Nr., Club-ID)..."
              data-testid="export-provider-search"
            />
          </div>
          {open && (
            <div style={{
              position: 'absolute', left: 0, right: 0, zIndex: 50, marginTop: 4,
              background: 'var(--card-bg, #fff)', border: '1px solid var(--border)', borderRadius: 8,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 240, overflowY: 'auto'
            }} data-testid="export-provider-dropdown">
              {filtered.length === 0 ? (
                <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>Kein Anbieter gefunden</div>
              ) : filtered.map(p => (
                <div key={p.id} onClick={() => { onChange(p.id); setQuery(''); setOpen(false); }} data-testid={`export-provider-option-${p.id}`}
                  style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.03)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <span style={{ fontWeight: 500 }}>{p.verein}</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)' }}>IK {p.ik_number} &middot; {p.club_id}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function ExportPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [search, setSearch] = useState('');
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const { apiFetch } = useApi();

  useEffect(() => {
    apiFetch('/api/providers').then(r => r?.json()).then(d => d && setProviders(d));
  }, []);

  useEffect(() => { loadPreview(); }, [selectedProvider]);

  const loadPreview = async () => {
    setLoading(true);
    const url = selectedProvider ? `/api/export/vdek-preview?provider_id=${selectedProvider}` : '/api/export/vdek-preview';
    const r = await apiFetch(url);
    const d = await r?.json();
    if (d) setData(d);
    setLoading(false);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('bgpr_token');
      const url = selectedProvider ? `${API}/api/export/vdek?provider_id=${selectedProvider}` : `${API}/api/export/vdek`;
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Download fehlgeschlagen');
      const blob = await res.blob();
      const disposition = res.headers.get('content-disposition');
      let filename = 'vdek_export.xlsx';
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      const urlObj = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlObj;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(urlObj);
    } catch (e) {
      console.error(e);
    }
    setDownloading(false);
  };

  const selectedProviderObj = providers.find(p => p.id === selectedProvider);

  const filtered = data?.rows?.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [r.verein, r.ik, r.angebot, r.veranstaltungsort_name, r.name_ubungsleiter, r.wochentag]
      .some(v => (v || '').toLowerCase().includes(q));
  }) || [];

  return (
    <div data-testid="export-page" style={{ maxWidth: '100%', overflow: 'hidden' }}>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2>vdek-Liste Export</h2>
          <p>
            {loading ? 'Daten werden geladen...' :
              selectedProvider
                ? `${data?.total || 0} zertifizierte Angebote von ${selectedProviderObj?.verein || 'Anbieter'}`
                : `${data?.total || 0} zertifizierte Angebote bereit zum Export`
            }
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={handleDownload}
          disabled={downloading || loading || !data?.total}
          data-testid="export-download-btn"
          style={{ flexShrink: 0 }}
        >
          {downloading ? <><Loader2 size={16} className="spin" /> Wird erstellt...</> :
            <><Download size={16} /> Excel herunterladen</>
          }
        </button>
      </div>

      {/* Provider filter */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-body" style={{ padding: '0.75rem 1.25rem' }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-secondary)', marginBottom: 6 }}>
            Anbieter filtern
          </div>
          <ProviderFilter providers={providers} value={selectedProvider} onChange={setSelectedProvider} />
        </div>
      </div>

      {/* Info banner */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-body" style={{ padding: '0.75rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <FileSpreadsheet size={20} style={{ color: 'var(--primary)', flexShrink: 0 }} />
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            <strong style={{ color: 'var(--text-primary)' }}>
              {selectedProvider ? 'Anbieter-Export' : 'Gesamt-Export'}
            </strong> — {selectedProvider
              ? 'Es werden nur die Angebote des ausgewählten Anbieters exportiert.'
              : 'Die Tabelle zeigt eine kompakte Vorschau der Daten. Die heruntergeladene Excel-Datei enthält alle 31 Spalten im vdek-Format.'}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-body" style={{ padding: '0.625rem 1rem' }}>
          <div className="search-input">
            <Search size={16} />
            <input
              className="form-input"
              placeholder="Vorschau durchsuchen..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="export-search"
              style={{ border: 'none', boxShadow: 'none', padding: '4px 0' }}
            />
          </div>
        </div>
      </div>

      {/* Preview Table */}
      <div className="card">
        {loading ? (
          <div className="empty-state"><Loader2 size={32} className="spin" /><p>Daten werden geladen...</p></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state"><FileSpreadsheet size={40} /><p>{selectedProvider ? 'Keine zertifizierten Angebote für diesen Anbieter' : 'Keine Daten gefunden'}</p></div>
        ) : (
          <div className="table-wrap" style={{ overflowX: 'auto' }}>
            <table data-testid="export-preview-table" style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th style={{ width: '35%' }}>Verein</th>
                  <th style={{ width: '15%' }}>Angebot</th>
                  <th style={{ width: '15%' }}>Wochentag</th>
                  <th style={{ width: '12%' }}>Uhrzeit</th>
                  <th style={{ width: '23%' }}>Ort</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, idx) => (
                  <tr key={idx} data-testid={`export-row-${idx}`}>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{idx + 1}</td>
                    <td style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.verein}>{r.verein || '-'}</td>
                    <td>{r.angebot || '-'}</td>
                    <td>{r.wochentag || '-'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{r.uhrzeit_von || '-'} – {r.uhrzeit_bis || '-'}</td>
                    <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.veranstaltungsort_name}>{r.veranstaltungsort_name || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <div style={{ padding: '0.75rem 1.5rem', borderTop: '1px solid var(--border)', fontSize: 13, color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between' }}>
            <span>Zeige {filtered.length} von {data?.total || 0} Einträgen</span>
            <span>Excel enthält 31 Spalten</span>
          </div>
        )}
      </div>
    </div>
  );
}
